// Редкие действия над учётной записью — под одной кнопкой, а не восемью в ряд.
//
// ## Зачем
//
// 13.09.2026 заказчик написал, что процесс сложный и «надо проще». Он прав, и дело было не в
// формулировках: в строке каждого человека стояло ВОСЕМЬ кнопок подряд — роли, карточка, вход
// в программу, фраза, закрытие доступа, удаление, код, отзыв кода. У него в списке два десятка
// человек, то есть полторы сотни кнопок на экране. Из них он нажимает одну, и то не каждый
// день: дать человеку код.
//
// Поэтому снаружи остаётся ровно одно частое действие, а остальные живут здесь, списком
// словами. Человек, которому нужен код, не видит ничего лишнего; человек, которому нужна роль,
// открывает «Ещё» и читает семь понятных строк вместо ряда кнопок одного веса.
//
// ## Почему не серые кнопки и не значки
//
// Значок вместо слова экономит место и стоит понимания: «карандаш» у роли и «карандаш» у фразы
// не различаются вовсе. Здесь у каждого пункта своё слово целиком, и порядок один и тот же у
// всех людей — тогда рука запоминает место, а не читает заново.

import { Fragment, useState } from 'react';

import { RiMoreLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import * as Popover from '@/shared/ui/popover';
import { cn } from '@/utils/cn';

export interface ПунктДействия {
  /** Что написано человеку. Слово целиком, без сокращений и значков. */
  подпись: string;
  выполнить: () => void;
  /** Опасное — красным и отдельно внизу, за чертой. */
  опасное?: boolean;
  /**
   * Заголовок группы, к которой пункт относится: «Кто это и что видит», «Как входит».
   *
   * Добавлено 13.09.2026: заказчик сказал, что путается. Семь пунктов подряд одним столбцом
   * читаются как семь равных возможностей, а на деле они отвечают на три разных вопроса —
   * кто этот человек, как он входит и как его убрать. Три коротких заголовка превращают
   * список в оглавление, по которому глаз попадает сразу в нужную треть.
   */
  группа?: string;
}

export function МенюДействий({ пункты, подпись }: { пункты: ПунктДействия[]; подпись: string }) {
  // Открытием управляем сами, а не через `Popover.Close`. У той обёртки свой вид — крестик в
  // углу панели, — и через `asChild` она навязывает его содержимому: все пункты получали
  // `absolute right-4 top-4` и слипались в одну точку. Сборка при этом зелёная, а меню не
  // работает; поймано живым нажатием, не тестом разметки.
  const [открыто, setОткрыто] = useState(false);
  const обычные = пункты.filter((пункт) => пункт.опасное !== true);
  const опасные = пункты.filter((пункт) => пункт.опасное === true);

  if (пункты.length === 0) {
    return null;
  }

  return (
    <Popover.Root open={открыто} onOpenChange={setОткрыто}>
      <Popover.Trigger asChild>
        <Button.Root type='button' variant='neutral' mode='stroke' size='xxsmall'>
          <Button.Icon as={RiMoreLine} />
          Ещё
        </Button.Root>
      </Popover.Trigger>
      {/* Появление БЕЗ движения. Набор оформления открывает панель наездом и увеличением, и
          на узком экране планшета пункт успевает уехать из-под пальца: нажатие по «Изменить
          роли» попадало в «Удалить из системы». Поймано живым прогоном на планшете, не тестом
          разметки. Движение здесь и не несёт смысла: меню либо открыто, либо нет. */}
      <Popover.Content
        align='end'
        sideOffset={6}
        showArrow={false}
        className='p-1 duration-0 data-[state=closed]:animate-none data-[state=open]:animate-none'
      >
        {/* Имя человека в шапке меню: открытых меню на экране одно, но список длинный, и
            «у кого я сейчас меняю роль» — вопрос, на который экран обязан отвечать сам. */}
        <p className='px-3 pb-1 pt-2 text-paragraph-xs text-text-sub-600'>{подпись}</p>
        <div className='flex min-w-52 flex-col'>
          <Группы пункты={обычные} закрыть={() => setОткрыто(false)} />
          {/* Черта говорит «дальше другое», и говорить ей есть о чём только тогда, когда выше
              что-то есть. Меню из одного опасного пункта бывает: у человека без живого кода,
              которого можно только удалить, и у выведенной из обращения формулировки помощника
              — там остаётся одно «Убрать». Черта под самой шапкой означала бы, что опасное
              начинается после имени, то есть ничего. */}
          {обычные.length > 0 && опасные.length > 0 && (
            <div className='my-1 h-px bg-stroke-soft-200' />
          )}
          {/* Заголовок нужен и над опасными: черта говорит «дальше другое», но не говорит,
              что именно. «Убрать» отвечает на это одним словом. */}
          <Группы пункты={опасные} закрыть={() => setОткрыто(false)} />
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

/** Список пунктов с заголовком над каждой сменой группы. */
function Группы({ пункты, закрыть }: { пункты: ПунктДействия[]; закрыть: () => void }) {
  return (
    <>
      {пункты.map((пункт, номер) => (
        <Fragment key={пункт.подпись}>
          {/* Заголовок пишется только при СМЕНЕ группы: повторённый над каждой строкой, он
              превратился бы в тот же шум, ради снятия которого заведён. */}
          {пункт.группа !== undefined && пункт.группа !== пункты[номер - 1]?.группа && (
            <p className='px-3 pb-1 pt-2 text-paragraph-xs text-text-sub-600'>
              {пункт.группа}
            </p>
          )}
          <Пункт пункт={пункт} закрыть={закрыть} />
        </Fragment>
      ))}
    </>
  );
}

function Пункт({ пункт, закрыть }: { пункт: ПунктДействия; закрыть: () => void }) {
  return (
    <button
      type='button'
      onClick={() => {
        закрыть();
        пункт.выполнить();
      }}
      // Высота честная, а не расширенная областью нажатия. Класс «цель-тача» рисует
      // невидимый прямоугольник 44 точки ПО ЦЕНТРУ элемента, и в вертикальном списке
      // соседние такие прямоугольники налезают друг на друга: нажатие по «Изменить роли»
      // попадало в лежащее ниже «Удалить из системы». Он придуман для мелких кнопок в
      // плотной сетке, где места нет; здесь место есть, и пальцу надо дать настоящие 44.
      className={cn(
        'flex min-h-11 items-center rounded-lg px-3 text-left text-paragraph-sm',
        'transition-colors hover:bg-bg-weak-50',
        пункт.опасное === true ? 'text-error-base' : 'text-text-strong-950',
      )}
    >
      {пункт.подпись}
    </button>
  );
}
